import {type MediaCardType} from '@wandb/weave/common/types/media';
import {useCallback, useMemo} from 'react';

import {
  type SettingType,
  trackSetting,
} from '../../../services/analytics/workspaceSettingsEvents';
import {useMemoizedSelector} from '../../../state/hooks';
import {useActiveHistoryKeyInfo} from '../../../state/panelGenHistoryKeys/useActiveHistoryKeyInfo';
import {useMaybeViewAction, useViewAction} from '../../../state/views/hooks';
import * as PanelBankConfigActions from '../../../state/views/panelBankConfig/actions';
import {makeMaybeHasPanelTypeSelector} from '../../../state/views/panelBankConfig/selectors';
import {makeSectionMediaKeysSelector} from '../../../state/views/panelBankSectionConfig/selectors';
import * as WorkspaceSettingsActions from '../../../state/views/workspaceSettings/actions';
import {
  makeWorkspaceLayoutSettingsSelector,
  makeWorkspaceSettingsAutoOrganizePrefixSelector,
  makeWorkspaceSettingsColorPaletteOptionSelector,
  makeWorkspaceSettingsCoordinateZoomingSelector,
  makeWorkspaceSettingsLinePlotSelector,
  makeWorkspaceSettingsMediaSelector,
  makeWorkspaceSettingsSemanticLegendPaletteSelector,
  makeWorkspaceSettingsSemanticLegendRunColorOptionsSelector,
  makeWorkspaceSettingsShouldAutoGeneratePanelsSelector,
  makeWorkspaceSettingsShowEmptySectionsSelector,
  makeWorkspaceSettingsShowXRangeSelector,
  makeWorkspaceSettingsSortAlphabeticallySelector,
  makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector,
  makeWorkspaceSettingsVideoPlaybackSyncLoopSelector,
} from '../../../state/views/workspaceSettings/selectors';
import {
  useSetSectionSyncSliderSteps,
  useSetWorkspaceSyncSliderSteps,
} from '../../PanelMediaBrowser/hooks/useSyncSliderSteps';
import {useMediaCardTypes} from '../../PanelMediaBrowser/useGetMediaCardTypes';
import {getMediaKeys} from '../../PanelMediaBrowser/utils/getMediaKeys';
import {
  useMaybeWorkspaceRefsContext,
  useWorkspaceRefsContext,
} from '../../Workspace/WorkspaceRefsContext';
import {useWorkspaceLinePlotSettings} from './runLinePlots/utils/getLinePlotSettings';
import {
  useSectionSettingsContext,
  useSectionSettingsContextMaybe,
} from './SectionSettingsContextProvider';

export {
  useCascadingSectionMediaActualSize,
  useCascadingSectionMediaAlwaysOriginal,
  useCascadingSectionMediaBoundingBoxConfig,
  useCascadingSectionMediaColumnCount,
  useCascadingSectionMediaCompareSettings,
  useCascadingSectionMediaDisableVideoStream,
  useCascadingSectionMediaFitToDimension,
  useCascadingSectionMediaGallerySettings,
  useCascadingSectionMediaGridSettings,
  useCascadingSectionMediaMaxGalleryItems,
  useCascadingSectionMediaMaxRuns,
  useCascadingSectionMediaMaxYAxisCount,
  useCascadingSectionMediaMode,
  useCascadingSectionMediaObject3DBackgroundColor,
  useCascadingSectionMediaPixelated,
  useCascadingSectionMediaPointColor,
  useCascadingSectionMediaSegmentationMaskConfig,
  useCascadingSectionMediaSliderKey,
  useCascadingSectionMediaSliderSync,
  useCascadingSectionMediaSnapToExistingStep,
  useCascadingSectionMediaStepStrideLength,
  useCascadingSectionMediaUseRightHandedSystem,
  useCascadingSectionMediaVideoActualSize,
  useCascadingSectionMediaVideoPlaybackSync,
  useCascadingSectionMediaVideoPlaybackSyncAutoplay,
  useCascadingSectionMediaVideoPlaybackSyncLoop,
  useCascadingSectionMediaVideoPlayerTimeStep,
  useMaybeCascadingSectionMediaActualSize,
  useMaybeCascadingSectionMediaAlwaysOriginal,
  useMaybeCascadingSectionMediaBoundingBoxConfig,
  useMaybeCascadingSectionMediaColumnCount,
  useMaybeCascadingSectionMediaCompareSettings,
  useMaybeCascadingSectionMediaDisableVideoStream,
  useMaybeCascadingSectionMediaFitToDimension,
  useMaybeCascadingSectionMediaGallerySettings,
  useMaybeCascadingSectionMediaGridSettings,
  useMaybeCascadingSectionMediaMaxGalleryItems,
  useMaybeCascadingSectionMediaMaxRuns,
  useMaybeCascadingSectionMediaMaxYAxisCount,
  useMaybeCascadingSectionMediaMode,
  useMaybeCascadingSectionMediaObject3DBackgroundColor,
  useMaybeCascadingSectionMediaPixelated,
  useMaybeCascadingSectionMediaPointColor,
  useMaybeCascadingSectionMediaSegmentationMaskConfig,
  useMaybeCascadingSectionMediaSliderKey,
  useMaybeCascadingSectionMediaSliderSync,
  useMaybeCascadingSectionMediaSnapToExistingStep,
  useMaybeCascadingSectionMediaStepStrideLength,
  useMaybeCascadingSectionMediaUseRightHandedSystem,
  useMaybeCascadingSectionMediaVideoActualSize,
  useMaybeCascadingSectionMediaVideoPlaybackSync,
  useMaybeCascadingSectionMediaVideoPlaybackSyncAutoplay,
  useMaybeCascadingSectionMediaVideoPlaybackSyncLoop,
  useMaybeCascadingSectionMediaVideoPlayerTimeStep,
  useMaybeSectionMediaActualSize,
  useMaybeSectionMediaAlwaysOriginal,
  useMaybeSectionMediaBoundingBoxConfig,
  useMaybeSectionMediaColumnCount,
  useMaybeSectionMediaCompareSettings,
  useMaybeSectionMediaDisableVideoStream,
  useMaybeSectionMediaFitToDimension,
  useMaybeSectionMediaGallerySettings,
  useMaybeSectionMediaGridSettings,
  useMaybeSectionMediaMaxGalleryItems,
  useMaybeSectionMediaMaxRuns,
  useMaybeSectionMediaMaxYAxisCount,
  useMaybeSectionMediaMode,
  useMaybeSectionMediaObject3DBackgroundColor,
  useMaybeSectionMediaPixelated,
  useMaybeSectionMediaPointColor,
  useMaybeSectionMediaSegmentationMaskConfig,
  useMaybeSectionMediaSliderKey,
  useMaybeSectionMediaSliderSync,
  useMaybeSectionMediaSnapToExistingStep,
  useMaybeSectionMediaStepStrideLength,
  useMaybeSectionMediaUseRightHandedSystem,
  useMaybeSectionMediaVideoActualSize,
  useMaybeSectionMediaVideoPlaybackSync,
  useMaybeSectionMediaVideoPlaybackSyncAutoplay,
  useMaybeSectionMediaVideoPlaybackSyncLoop,
  useMaybeSectionMediaVideoPlayerTimeStep,
  useMaybeWorkspaceMediaActualSize,
  useMaybeWorkspaceMediaAlwaysOriginal,
  useMaybeWorkspaceMediaBoundingBoxConfig,
  useMaybeWorkspaceMediaColumnCount,
  useMaybeWorkspaceMediaCompareSettings,
  useMaybeWorkspaceMediaDisableVideoStream,
  useMaybeWorkspaceMediaFitToDimension,
  useMaybeWorkspaceMediaGallerySettings,
  useMaybeWorkspaceMediaGridSettings,
  useMaybeWorkspaceMediaMaxGalleryItems,
  useMaybeWorkspaceMediaMaxRuns,
  useMaybeWorkspaceMediaMaxYAxisCount,
  useMaybeWorkspaceMediaMode,
  useMaybeWorkspaceMediaObject3DBackgroundColor,
  useMaybeWorkspaceMediaPixelated,
  useMaybeWorkspaceMediaPointColor,
  useMaybeWorkspaceMediaSegmentationMaskConfig,
  useMaybeWorkspaceMediaSliderKey,
  useMaybeWorkspaceMediaSliderSync,
  useMaybeWorkspaceMediaSnapToExistingStep,
  useMaybeWorkspaceMediaStepStrideLength,
  useMaybeWorkspaceMediaUseRightHandedSystem,
  useMaybeWorkspaceMediaVideoActualSize,
  useMaybeWorkspaceMediaVideoPlaybackSync,
  useMaybeWorkspaceMediaVideoPlaybackSyncAutoplay,
  useMaybeWorkspaceMediaVideoPlaybackSyncLoop,
  useMaybeWorkspaceMediaVideoPlayerTimeStep,
  useMediaActualSizeDisplay,
  useMediaAlwaysOriginalDisplay,
  useMediaBoundingBoxConfigDisplay,
  useMediaColumnCountDisplay,
  useMediaCompareSettingsDisplay,
  useMediaDisableVideoStreamDisplay,
  useMediaFitToDimensionDisplay,
  useMediaGallerySettingsDisplay,
  useMediaGridSettingsDisplay,
  useMediaMaxGalleryItemsDisplay,
  useMediaMaxRunsDisplay,
  useMediaMaxYAxisCountDisplay,
  useMediaModeDisplay,
  useMediaMoleculeConfigDisplay,
  useMediaObject3DBackgroundColorDisplay,
  useMediaPixelatedDisplay,
  useMediaPointColorDisplay,
  useMediaSegmentationMaskConfigDisplay,
  useMediaSliderKeyDisplay,
  useMediaSliderSyncDisplay,
  useMediaSnapToExistingStepDisplay,
  useMediaStepStrideLengthDisplay,
  useMediaUseRightHandedSystemDisplay,
  useMediaVideoActualSizeDisplay,
  useMediaVideoPlaybackSyncAutoplayDisplay,
  useMediaVideoPlaybackSyncDisplay,
  useMediaVideoPlaybackSyncIsInherited,
  useMediaVideoPlaybackSyncLoopDisplay,
  useMediaVideoPlayerTimeStepDisplay,
  usePanelMediaChartTitle,
  usePanelMediaKeys,
  useSectionMediaActualSize,
  useSectionMediaAlwaysOriginal,
  useSectionMediaBoundingBoxConfig,
  useSectionMediaColumnCount,
  useSectionMediaCompareSettings,
  useSectionMediaDisableVideoStream,
  useSectionMediaFitToDimension,
  useSectionMediaGallerySettings,
  useSectionMediaGridSettings,
  useSectionMediaMaxGalleryItems,
  useSectionMediaMaxRuns,
  useSectionMediaMaxYAxisCount,
  useSectionMediaMode,
  useSectionMediaObject3DBackgroundColor,
  useSectionMediaPixelated,
  useSectionMediaPointColor,
  useSectionMediaSegmentationMaskConfig,
  useSectionMediaSliderKey,
  useSectionMediaSliderSync,
  useSectionMediaSnapToExistingStep,
  useSectionMediaStepStrideLength,
  useSectionMediaUseRightHandedSystem,
  useSectionMediaVideoActualSize,
  useSectionMediaVideoPlaybackSync,
  useSectionMediaVideoPlaybackSyncAutoplay,
  useSectionMediaVideoPlaybackSyncLoop,
  useSectionMediaVideoPlayerTimeStep,
  useShouldShowMediaActualSizeResetAtLevel,
  useShouldShowMediaAlwaysOriginalResetAtLevel,
  useShouldShowMediaBoundingBoxConfigResetAtLevel,
  useShouldShowMediaChartTitleResetAtLevel,
  useShouldShowMediaColumnCountResetAtLevel,
  useShouldShowMediaCompareSettingsResetAtLevel,
  useShouldShowMediaDisableVideoStreamResetAtLevel,
  useShouldShowMediaFitToDimensionResetAtLevel,
  useShouldShowMediaGallerySettingsResetAtLevel,
  useShouldShowMediaGridSettingsResetAtLevel,
  useShouldShowMediaKeysResetAtLevel,
  useShouldShowMediaMaxGalleryItemsResetAtLevel,
  useShouldShowMediaMaxRunsResetAtLevel,
  useShouldShowMediaMaxYAxisCountResetAtLevel,
  useShouldShowMediaModeResetAtLevel,
  useShouldShowMediaMoleculeConfigResetAtLevel,
  useShouldShowMediaObject3DBackgroundColorResetAtLevel,
  useShouldShowMediaPixelatedResetAtLevel,
  useShouldShowMediaPointColorResetAtLevel,
  useShouldShowMediaSegmentationMaskConfigResetAtLevel,
  useShouldShowMediaSliderKeyResetAtLevel,
  useShouldShowMediaSliderSyncResetAtLevel,
  useShouldShowMediaSnapToExistingStepResetAtLevel,
  useShouldShowMediaStepStrideLengthResetAtLevel,
  useShouldShowMediaUseRightHandedSystemResetAtLevel,
  useShouldShowMediaVideoActualSizeResetAtLevel,
  useShouldShowMediaVideoPlaybackSyncAutoplayResetAtLevel,
  useShouldShowMediaVideoPlaybackSyncLoopResetAtLevel,
  useShouldShowMediaVideoPlaybackSyncResetAtLevel,
  useShouldShowMediaVideoPlayerTimeStepResetAtLevel,
  useWorkspaceMediaActualSize,
  useWorkspaceMediaAlwaysOriginal,
  useWorkspaceMediaBoundingBoxConfig,
  useWorkspaceMediaColumnCount,
  useWorkspaceMediaCompareSettings,
  useWorkspaceMediaDisableVideoStream,
  useWorkspaceMediaFitToDimension,
  useWorkspaceMediaGallerySettings,
  useWorkspaceMediaGridSettings,
  useWorkspaceMediaMaxGalleryItems,
  useWorkspaceMediaMaxRuns,
  useWorkspaceMediaMaxYAxisCount,
  useWorkspaceMediaMode,
  useWorkspaceMediaObject3DBackgroundColor,
  useWorkspaceMediaPixelated,
  useWorkspaceMediaPointColor,
  useWorkspaceMediaSegmentationMaskConfig,
  useWorkspaceMediaSliderKey,
  useWorkspaceMediaSliderSync,
  useWorkspaceMediaSnapToExistingStep,
  useWorkspaceMediaStepStrideLength,
  useWorkspaceMediaUseRightHandedSystem,
  useWorkspaceMediaVideoActualSize,
  useWorkspaceMediaVideoPlaybackSync,
  useWorkspaceMediaVideoPlaybackSyncAutoplay,
  useWorkspaceMediaVideoPlaybackSyncLoop,
  useWorkspaceMediaVideoPlayerTimeStep,
} from './hooks/mediaSettingHooks';
export {useUpdateLayoutSettingAndOrganizePrefix} from './workspaceLayout/useUpdateOrganizePrefix';
export {useUpdateShouldAutoGeneratePanels} from './workspaceLayout/useUpdateShouldAutoGeneratePanels';
export {useUpdateLayoutSettingAndSortPanels} from './workspaceLayout/useUpdateSortPanels';

export function useUpdateLinePlotWorkspaceSettings() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateLinePlotWorkspaceSettings
  );
}

export function useUpdateWorkspaceLayoutSettings() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateWorkspaceLayoutSettings
  );
}

export function useUpdateWorkspaceMediaSettings() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateWorkspaceMediaSettings
  );
}

export function useUnsetWorkspaceMediaSetting() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.unsetWorkspaceMediaSetting
  );
}

export function useUnsetWorkspaceMediaSettings() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.unsetWorkspaceMediaSettings
  );
}

export function useSetWorkspaceSliderSteps() {
  const {panelBankConfigRef, workspaceSettingsRef} = useWorkspaceRefsContext();
  return useSetWorkspaceSyncSliderSteps(
    workspaceSettingsRef,
    panelBankConfigRef
  );
}

export function useMaybeSetWorkspaceSliderSteps():
  | ReturnType<typeof useSetWorkspaceSyncSliderSteps>
  | undefined {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const setWorkspaceSliderSteps = useSetWorkspaceSyncSliderSteps(
    workspaceRefs?.workspaceSettingsRef,
    workspaceRefs?.panelBankConfigRef
  );

  return workspaceRefs == null ? undefined : setWorkspaceSliderSteps;
}

export function useSetSectionSliderSteps() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;

  return useSetSectionSyncSliderSteps(workspaceSettingsRef, sectionRef);
}

export function useMaybeSetSectionSliderSteps():
  | ReturnType<typeof useSetSectionSyncSliderSteps>
  | undefined {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const setSectionSliderSteps = useSetSectionSyncSliderSteps(
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );

  return sectionRef == null ? undefined : setSectionSliderSteps;
}

export function useUpdateAllLinePlotSectionSettings() {
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  return useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.updateAllLinePlotSectionSettings
  );
}

export function useUpdateColorPaletteOption() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateColorPaletteOption
  );
}

export function useMaybeUpdateColorPaletteOption() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMaybeViewAction(
    workspaceRefs?.workspaceSettingsRef,
    WorkspaceSettingsActions.updateColorPaletteOption
  );
}

export function useUpdateSemanticLegendPalette() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateSemanticLegendPalette
  );
}

export function useMaybeUpdateSemanticLegendPalette() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMaybeViewAction(
    workspaceRefs?.workspaceSettingsRef,
    WorkspaceSettingsActions.updateSemanticLegendPalette
  );
}

export function useLinePlot() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsLinePlotSelector,
    workspaceSettingsRef
  );
}

export function useCoordinateZooming() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsCoordinateZoomingSelector,
    workspaceSettingsRef
  );
}

export function useLinePlotSettings() {
  const linePlot = useLinePlot();
  return useWorkspaceLinePlotSettings({linePlot});
}

export function useSemanticLegendRunColorOptions() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsSemanticLegendRunColorOptionsSelector,
    workspaceSettingsRef
  );
}

export function useMaybeSemanticLegendRunColorOptions() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsSemanticLegendRunColorOptionsSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

export function useShowEmptySections() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsShowEmptySectionsSelector,
    workspaceSettingsRef
  );
}

export function useAutoOrganizePrefix() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsAutoOrganizePrefixSelector,
    workspaceSettingsRef
  );
}

export function useWorkspaceLayoutSettings() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceLayoutSettingsSelector,
    workspaceSettingsRef
  );
}

export function useSortAlphabetically() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsSortAlphabeticallySelector,
    workspaceSettingsRef
  );
}

export function useShouldAutoGeneratePanels() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsShouldAutoGeneratePanelsSelector,
    workspaceSettingsRef
  );
}

export function useWorkspaceMediaSettings() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsMediaSelector,
    workspaceSettingsRef
  );
}

export function useMaybeVideoPlaybackSyncAutoplay() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const value = useMemoizedSelector(
    makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector,
    workspaceRefs?.workspaceSettingsRef
  );
  return value ?? false;
}

export function useMaybeVideoPlaybackSyncLoop() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const value = useMemoizedSelector(
    makeWorkspaceSettingsVideoPlaybackSyncLoopSelector,
    workspaceRefs?.workspaceSettingsRef
  );
  return value ?? false;
}

export function useMaybeLinePlotShowXRange() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsShowXRangeSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

export function useColorPaletteOption() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsColorPaletteOptionSelector,
    workspaceSettingsRef
  );
}

export function useMaybeColorPaletteOption() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsColorPaletteOptionSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

export function useSemanticLegendPalette() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsSemanticLegendPaletteSelector,
    workspaceSettingsRef
  );
}

export function useMaybeSemanticLegendPalette() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsSemanticLegendPaletteSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

export function useMediaKeysAndCardTypes(): {
  mediaKeys: string[];
  mediaCardTypes: MediaCardType[];
} {
  // Serves the sampled panel-gen keys under NGW, where the full history-keys
  // query is skipped, so this reads one source either way.
  const {historyKeyInfo: keyInfo} = useActiveHistoryKeyInfo();
  const mediaKeys = useMemo(() => {
    const {allMediaKeys} = getMediaKeys(keyInfo);
    return allMediaKeys;
  }, [keyInfo]);
  const mediaCardTypes = useMediaCardTypes(mediaKeys) ?? [];
  return {mediaKeys, mediaCardTypes};
}

export function useSectionMediaKeys(): string[] {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(makeSectionMediaKeysSelector, sectionRef);
}

export function useMaybeSectionMediaKeys(): string[] {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(makeSectionMediaKeysSelector, sectionRef);
}

export function useSectionMediaCardTypes(): MediaCardType[] {
  const mediaKeys = useSectionMediaKeys();
  return useMediaCardTypes(mediaKeys) ?? [];
}

export function useMaybeSectionMediaCardTypes(): MediaCardType[] {
  const mediaKeys = useMaybeSectionMediaKeys();
  return useMediaCardTypes(mediaKeys) ?? [];
}

export function useMaybeSectionMediaKeysAndCardTypes(): {
  mediaKeys: string[];
  mediaCardTypes: MediaCardType[];
} {
  const mediaKeys = useMaybeSectionMediaKeys();
  const mediaCardTypes = useMediaCardTypes(mediaKeys) ?? [];
  return {mediaKeys, mediaCardTypes};
}

export function useMaybeHasMediaPanel(): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeMaybeHasPanelTypeSelector,
    workspaceRefs?.panelBankConfigRef,
    'Media Browser'
  );
}

export function useTrackWorkspaceSettingChange() {
  return useCallback((setting: SettingType, value: string | undefined) => {
    trackSetting({
      action: 'change',
      page: 'workspace',
      settingLevel: 'workspace',
      setting,
      settingValue: value,
    });
  }, []);
}

export function useTrackSectionSettingChange() {
  return useCallback((setting: SettingType, value: string | undefined) => {
    trackSetting({
      action: 'change',
      page: 'workspace',
      settingLevel: 'section',
      setting,
      settingValue: value,
    });
  }, []);
}
